import { Plus, Filter, Search } from 'lucide-react';
import { Card, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Input } from '../../components/common/Input';
import type { Proposal } from '../../types/governance';

const mockProposals: Proposal[] = [
  {
    id: '1',
    title: 'Update Ecosystem Fee Structure',
    description: 'Proposal to reduce transaction fees for verified developers to encourage more app submissions.',
    authorId: 'u1',
    authorName: 'DevCommunity',
    status: 'active',
    votesFor: 1240,
    votesAgainst: 450,
    endTime: '2026-04-15T00:00:00Z',
    category: 'Economics',
  },
  {
    id: '2',
    title: 'New Community Guidelines v2.0',
    description: 'Comprehensive update to safety and moderation policies for all social circles.',
    authorId: 'u2',
    authorName: 'SafetyBoard',
    status: 'active',
    votesFor: 890,
    votesAgainst: 120,
    endTime: '2026-04-10T00:00:00Z',
    category: 'Policy',
  },
  {
    id: '3',
    title: 'Integrate Biometric Auth for High-Value Actions',
    description: 'Mandatory biometric verification for all governance votes and financial transfers.',
    authorId: 'u3',
    authorName: 'SecurityTeam',
    status: 'passed',
    votesFor: 2500,
    votesAgainst: 50,
    endTime: '2026-03-25T00:00:00Z',
    category: 'Security',
  },
];

export const Governance = () => {
  return (
    <div className="space-y-8">
      <header className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Governance</h1>
          <p className="text-slate-500">Participate in the decision-making process of the Keskedo ecosystem.</p>
        </div>
        <Button className="w-full md:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          New Proposal
        </Button>
      </header>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-slate-500 uppercase">Active Proposals</p>
            <h3 className="text-2xl font-bold text-slate-900">12</h3>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-slate-500 uppercase">Total Votes Cast</p>
            <h3 className="text-2xl font-bold text-slate-900">45.2k</h3>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-slate-500 uppercase">Your Voting Power</p>
            <h3 className="text-2xl font-bold text-indigo-600">750 VP</h3>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-x-4 md:space-y-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search proposals..." className="pl-10" />
        </div>
        <Button variant="outline" size="md">
          <Filter className="mr-2 h-4 w-4" />
          Filter
        </Button>
      </div>

      <div className="grid gap-6">
        {mockProposals.map((proposal) => (
          <Card key={proposal.id} hover className="cursor-pointer">
            <div className="flex flex-col space-y-4 md:flex-row md:items-start md:justify-between md:space-y-0">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Badge variant={proposal.status === 'active' ? 'primary' : 'success'}>
                    {proposal.status.toUpperCase()}
                  </Badge>
                  <span className="text-xs text-slate-500">{proposal.category}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900">{proposal.title}</h3>
                <p className="text-slate-600 line-clamp-2">{proposal.description}</p>
                <div className="flex items-center space-x-4 text-sm text-slate-500">
                  <span>By {proposal.authorName}</span>
                  <span>•</span>
                  <span>Ends {new Date(proposal.endTime).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="flex flex-col space-y-4 md:w-64">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-slate-700">Progress</span>
                    <span className="text-slate-500">{Math.round((proposal.votesFor / (proposal.votesFor + proposal.votesAgainst)) * 100)}% For</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div 
                      className="h-full bg-indigo-600" 
                      style={{ width: `${(proposal.votesFor / (proposal.votesFor + proposal.votesAgainst)) * 100}%` }} 
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" className="flex-1">View</Button>
                  {proposal.status === 'active' && <Button className="flex-1">Vote</Button>}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
